/**
 * Sentry init for the web Mini App.
 *
 * Imported from main.tsx BEFORE React renders so unhandled errors during
 * initial load are captured. Skipped entirely in Coming Soon mode (static
 * marketing page — nothing to monitor) and when no DSN is configured (e.g.
 * local dev without VITE_SENTRY_DSN in .env.local).
 *
 * Environment + release are set at build time:
 *   - VITE_SENTRY_DSN          — public-ish, bundled into JS, OK by design
 *   - VITE_SENTRY_ENVIRONMENT  — "development" | "staging" | "production"
 *   - VITE_SENTRY_RELEASE      — git SHA from CI; allows source map matching
 *
 * Sample rates (rec. by sentry docs, tuned per-env):
 *   - errors: always captured (sampleRate=1.0)
 *   - traces: 100% in staging (debug), 10% in prod (cost)
 *   - replays: 10% sessions in staging, 5% in prod; ALWAYS on error
 *
 * Privacy:
 *   - sendDefaultPii: false → don't auto-attach IPs / cookies / etc.
 *   - beforeSend strips `Authorization: tma ...` from breadcrumbs (it
 *     contains initData with Telegram user info: first_name, username,
 *     photo_url, language_code, is_premium).
 *   - Replay: maskAllText + blockAllMedia — recording shows UI structure
 *     and interactions but never user-visible text or images. Good
 *     middle ground: still useful for repro, no PII leak.
 */

import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development';
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE;

export function initSentry(): void {
  // No DSN → no-op. Lets `npm run dev` work without an .env.local entry
  // (errors just print to console as usual).
  if (!DSN) {
    console.info('[sentry] disabled (no VITE_SENTRY_DSN)');
    return;
  }

  const isProd = ENVIRONMENT === 'production';

  Sentry.init({
    dsn: DSN,
    environment: ENVIRONMENT,
    release: RELEASE,

    // Default integrations include browser tracing + replay; we tune them
    // explicitly below. `sendDefaultPii: false` is the package default
    // but we pin it for clarity / future-proofing.
    sendDefaultPii: false,

    // Traces sample — prod is cost-sensitive, staging is debug-friendly.
    tracesSampleRate: isProd ? 0.1 : 1.0,

    // Replay sample — sessions captured at low rate, but EVERY error
    // session gets a replay attached (replaysOnErrorSampleRate=1.0).
    // That's the high-leverage one: when something breaks, you see exactly
    // what the user did. Random session capture is just for baseline UX
    // (rage clicks, etc.).
    replaysSessionSampleRate: isProd ? 0.05 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      // Privacy-aware replay. With our masking config, the recording shows
      // layout + interactions but every text node is masked and every
      // image/video is blocked. Safe for TMA where uploaded photos +
      // Telegram user names appear on screen.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserTracingIntegration(),
    ],

    // Strip sensitive headers before transmission. Breadcrumbs include
    // outgoing fetch requests (URL + headers), so `Authorization: tma <initData>`
    // would otherwise be uploaded to Sentry. initData encodes Telegram user
    // info — we never want it server-side beyond our Worker's auth check.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        const data = breadcrumb.data as
          | { request_headers?: Record<string, string> }
          | undefined;
        if (data?.request_headers?.Authorization) {
          data.request_headers.Authorization = '[Filtered]';
        }
      }
      return breadcrumb;
    },

    beforeSend(event) {
      // Same scrub on request context attached directly to events
      // (synthetic events, manual captures via Sentry.captureException
      // with a request hint, etc.).
      if (event.request?.headers) {
        const headers = event.request.headers as Record<string, string>;
        if (headers.Authorization) headers.Authorization = '[Filtered]';
        if (headers.authorization) headers.authorization = '[Filtered]';
      }
      return event;
    },
  });
}
